import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ETypeOfNotification, setNotification } from 'medere-ui-framework';
import { loadAllEstablishments } from '../servicesHooks/Location/useLocationConfigService';
import {
    getCashRegister,
    getCashRegisterFinancialAccountsByPaymentMethod,
    getMyActiveSession,
} from '../services/LocationCashController';
import { getBalance as getFinancialAccountBalance } from '../services/FinancialAccountController';
import { parseApiError } from '../services/apiError';
import { CashRegisterDTO, CashRegisterFinancialAccountDTO } from '../dto/locationcash/LocationCashDTO';
import { Box, Establishment } from '../interface/interface';

export type CashRegisterOption = CashRegisterDTO & { displayDescription: string };

/** Por qué no se puede pagar: el sitio maneja cajas y el usuario no tiene ninguna abierta. */
export const NO_OPEN_CASH_REGISTER_MESSAGE = 'No tenés una caja abierta. Abrila para registrar el pago.';

/** 'error' = no se sabe si el sitio maneja cajas o cuál tiene abierta el usuario: no se puede registrar el pago. */
export type CashRegistersStatus = 'loading' | 'loaded' | 'error';

interface UsePaymentOriginParams {
    /** Solo consulta cuentas y saldo mientras el formulario de pago está a la vista. */
    enabled: boolean;
    paymentMethodId: number | null | undefined;
}

/**
 * Origen de un egreso a un profesional: caja → cuenta financiera (según el medio de pago) → saldo disponible.
 * Lo comparten la rendición nueva y el pago adicional.
 * Si el sitio no tiene cajas activas, el origen no se pide (`requiresPaymentOrigin` en false) y el pago
 * se registra sin movimiento de caja ni de cuenta financiera.
 * Si el sitio tiene cajas, el pago solo puede salir de la caja que el usuario tiene abierta (lo exige el backend):
 * es la única opción y queda seleccionada. Sin caja abierta (`hasOpenCashRegister` en false) no se puede pagar.
 */
export function usePaymentOrigin({ enabled, paymentMethodId }: UsePaymentOriginParams) {
    const dispatch = useDispatch();

    const [siteHasCashRegisters, setSiteHasCashRegisters] = useState(false);
    const [openCashRegister, setOpenCashRegister] = useState<CashRegisterOption | null>(null);
    const [cashRegistersStatus, setCashRegistersStatus] = useState<CashRegistersStatus>('loading');
    const [cashRegister, setCashRegister] = useState<CashRegisterOption | null>(null);
    const [financialAccountOptions, setFinancialAccountOptions] = useState<CashRegisterFinancialAccountDTO[]>([]);
    const [financialAccount, setFinancialAccount] = useState<CashRegisterFinancialAccountDTO | null>(null);
    const [loadingFinancialAccounts, setLoadingFinancialAccounts] = useState(false);
    const [balance, setBalance] = useState<number | null>(null);
    const [loadingBalance, setLoadingBalance] = useState(false);

    useEffect(() => {
        let cancelled = false;

        async function loadCashRegisterOptions() {
            try {
                // Las cajas del sitio dicen si el sitio maneja cajas; la sesión, de cuál puede salir la plata
                const [establishments, session] = await Promise.all([loadAllEstablishments(), getMyActiveSession()]);
                const activeBoxes: Array<{ establishment: Establishment; box: Box }> = (establishments ?? [])
                    .filter(establishment => establishment.active)
                    .flatMap(establishment => (establishment.boxes ?? [])
                        .filter(box => box.active)
                        .map(box => ({ establishment, box })));

                const results = await Promise.allSettled(
                    activeBoxes.map(({ box }) => getCashRegister(box.hospitallocation))
                );
                const siteRegisters: CashRegisterOption[] = results.flatMap((result, index) => {
                    if (result.status !== 'fulfilled' || !result.value?.active) return [];
                    const { establishment, box } = activeBoxes[index];
                    return [{
                        ...result.value,
                        hospitalLocationId: box.hospitallocation,
                        displayDescription: `${result.value.description} — ${establishment.buildingname} / ${box.description}`,
                    }];
                });
                const open = session
                    ? siteRegisters.find(register => register.hospitalLocationId === session.hospitalLocationId) ?? null
                    : null;

                if (!cancelled) {
                    setSiteHasCashRegisters(siteRegisters.length > 0);
                    setOpenCashRegister(open);
                    setCashRegister(open);
                    setCashRegistersStatus('loaded');
                }
            } catch (error) {
                if (cancelled) return;
                parseApiError(error);
                setCashRegistersStatus('error');
                dispatch(setNotification({
                    title: 'No se pudieron cargar las cajas. Intentá de nuevo.',
                    icon: 'pi-times',
                    typeOfNotification: ETypeOfNotification.ERROR,
                }));
            }
        }

        loadCashRegisterOptions();
        return () => { cancelled = true; };
    }, [dispatch]);

    const registerId = cashRegister?.locationCashRegisterId;

    useEffect(() => {
        setFinancialAccount(null);
        if (!enabled || registerId == null || paymentMethodId == null) {
            setFinancialAccountOptions([]);
            return;
        }

        let cancelled = false;
        setLoadingFinancialAccounts(true);
        getCashRegisterFinancialAccountsByPaymentMethod(registerId, paymentMethodId)
            .then(accounts => {
                if (!cancelled) setFinancialAccountOptions(accounts ?? []);
            })
            .catch(() => {
                if (!cancelled) setFinancialAccountOptions([]);
            })
            .finally(() => {
                if (!cancelled) setLoadingFinancialAccounts(false);
            });

        return () => { cancelled = true; };
    }, [enabled, registerId, paymentMethodId]);

    const financialAccountId = financialAccount?.financialAccountId;

    useEffect(() => {
        setBalance(null);
        if (!enabled || financialAccountId == null) {
            setLoadingBalance(false);
            return;
        }

        let cancelled = false;
        setLoadingBalance(true);
        getFinancialAccountBalance(financialAccountId)
            .then(value => {
                if (!cancelled) setBalance(Number(value));
            })
            .catch(() => {
                if (!cancelled) setBalance(null);
            })
            .finally(() => {
                if (!cancelled) setLoadingBalance(false);
            });

        return () => { cancelled = true; };
    }, [enabled, financialAccountId]);

    // La caja abierta sigue elegida: es la única de la que puede salir el pago
    const reset = useCallback(() => {
        setCashRegister(openCashRegister);
        setFinancialAccount(null);
        setFinancialAccountOptions([]);
        setBalance(null);
    }, [openCashRegister]);

    const cashRegisterOptions = useMemo(() => (openCashRegister ? [openCashRegister] : []), [openCashRegister]);

    return {
        cashRegistersStatus,
        /** El sitio maneja cajas: el pago tiene que salir de una. Solo es válido con `cashRegistersStatus === 'loaded'`. */
        requiresPaymentOrigin: siteHasCashRegisters,
        /** El usuario tiene una caja abierta en el sitio; si el sitio maneja cajas y no la tiene, no puede pagar. */
        hasOpenCashRegister: openCashRegister != null,
        /** El sitio maneja cajas y el usuario no tiene ninguna abierta: el pago se previene (DS §3.8). */
        missingOpenCashRegister: cashRegistersStatus === 'loaded' && siteHasCashRegisters && openCashRegister == null,
        /** Solo la caja abierta del usuario (o ninguna). */
        cashRegisterOptions,
        cashRegister,
        setCashRegister,
        financialAccountOptions,
        financialAccount,
        setFinancialAccount,
        loadingFinancialAccounts,
        balance,
        loadingBalance,
        reset,
    };
}

/** Lo que devuelve {@link usePaymentOrigin}: lo recibe `PaymentOriginFields`. */
export type PaymentOrigin = ReturnType<typeof usePaymentOrigin>;
